import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'
import type { Language } from '~/types/Account'
import type { ManifestWebDomain } from '~/types/Theme'

const API_URL = window.origin + '/api'

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})
axiosInstance.interceptors.request.use(function (config) {
  const securityStore = useSecurityStore()
  config.headers.Authorization = `Bearer ${securityStore.apiToken}`
  return config
})
axiosInstance.interceptors.response.use(
  response => response,
  (error) => {
    const securityStore = useSecurityStore()
    if ((error.response && error.response.status === 401) && securityStore.isLoggedIn) {
      securityStore.unload()
      navigateTo('/login')
    }
    return Promise.reject(error)
  }
)
// interface AbortablePromise<T> extends Promise<T> {
//   /**
//    * Aborts the current request with a reason or not
//    */
//   abort: (reason?: unknown) => void
// }
/**
 * Adds cancellation support to standard promise
 */
class AbortablePromise<T> extends Promise<T> {
  private controller?: AbortController

  public abort(reason?: unknown) {
    this.controller?.abort(reason)
  }

  static wrap<T>(inner: Promise<T>, controller: AbortController): AbortablePromise<T> {
    const wrapped = new AbortablePromise<T>((resolve, reject) => {
      inner.then(resolve, reject)
    })
    wrapped.controller = controller
    return wrapped
  }
}
/**
 * Extends axios and add support for cancellation
 */
interface ClientApi {
  get<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, config?: AxiosRequestConfig<D>): AbortablePromise<R>
  delete<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, config?: AxiosRequestConfig<D>): AbortablePromise<R>
  post<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>): AbortablePromise<R>
  put<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>): AbortablePromise<R>
  patch<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>): AbortablePromise<R>
}

function makeRequest<T = unknown, R = AxiosResponse<T>, D = unknown>(config: AxiosRequestConfig<D>): AbortablePromise<R> {
  const abortController = new AbortController()
  const promise: Promise<R> = axiosInstance.request({
    ...config,
    signal: abortController.signal,
  })
  return AbortablePromise.wrap(promise, abortController)
}
export const api: ClientApi = {
  get<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, config?: AxiosRequestConfig<D>): AbortablePromise<R> {
    return makeRequest({
      url: url,
      method: 'GET',
      ...config,
    })
  },
  delete<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, config?: AxiosRequestConfig<D>): AbortablePromise<R> {
    return makeRequest({
      url: url,
      method: 'DELETE',
      ...config,
    })
  },
  post<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>): AbortablePromise<R> {
    return makeRequest({
      url: url,
      method: 'POST',
      data: data,
      ...config,
    })
  },
  put<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>): AbortablePromise<R> {
    return makeRequest({
      url: url,
      method: 'PUT',
      data: data,
      ...config,
    })
  },
  patch<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>): AbortablePromise<R> {
    return makeRequest({
      url: url,
      method: 'PATCH',
      data: data,
      ...config,
    })
  },
}

export function findAvailableLanguages() {
  return api.get<Language[]>('/languages/available')
}

export function findTheme() {
  return api.get<ManifestWebDomain>('/themes')
}
